import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import { Paths } from "../../../constants";
import { CareerCenterStatuses } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import Settings from "../../../settings";
import { AppState } from "../../../store";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    goto: (url: string) => {
        dispatch(push(url));
    },
    notifyInfo: (message: string, icon?: string) => dispatch(Actions.Noti.notifyInfo(message, icon)),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {
};

interface ConnectedState {
    api: Api;
}

interface ConnectedDispatch {
    goto: (url: string) => void;
    notifyInfo: (message: string, icon?: string) => void;
}

interface OwnState {
    errors: string[];
    jobPost?: M.CareerCenter.JobPost;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ReviewJobComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);
        this.state = {
            errors: [],
            loading: true,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        const guid = this.props.match.params.guid;
        if (!guid) {
            this.setState({
                ...this.state,
                errors: ["Job post identifier is missing in URL (guid)"],
            });
            return;
        }

        const qp = { queryParams: { guid } };
        const result = await this.props.api.get<M.CareerCenter.JobPost>(Api.endpoints.users.careerCenter.job, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            if (result.data.statusId !== CareerCenterStatuses.Created.id) {
                this.props.notifyInfo("The requested job post has already been posted.");
                this.props.goto(Paths.CareerCenter.Job.Confirmation + "/" + guid);
                return;
            }

            this.setState({
                ...this.state,
                jobPost: result.data,
            });
        }
        else {
            const errors =  M.Result.Result.errorsOrMessage(result);
            this.setState({
                ...this.state,
                errors,
            });
        }

        this.toggleLoading(false);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        let details = null;

        if (this.state.errors.length) {
            details = <BS.Alert bsStyle="warning">
                <h3>The requested job post could not be retrieved.</h3>
                <ul>
                    {this.state.errors.map((e, i) => (<li key={i}>{e.toString()}</li>))}
                </ul>
            </BS.Alert>;
        }
        else if (this.state.jobPost) {
                details = <ReviewJobForm
                api={this.props.api}
                jobPost={this.state.jobPost}
                onSuccess={(model) => this.onFinalizeSuccess(model)}
            />;
        }

        return <Comp.Page.PublicPage pageTitle="Review job post" style={{position: "relative", minHeight: "300px"}}>
            <Comp.Generic.LoadingSpinner show={this.state.loading} />
            <h1>Review</h1>
            {details}
        </Comp.Page.PublicPage>;
    }

    private onFinalizeSuccess(job: M.CareerCenter.JobPost) {
        this.props.goto(`${Paths.CareerCenter.Job.Confirmation}/${job.guid}`);
    }
}

export const ReviewJob: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ReviewJobComponent) as any);

interface ReviewJobFormProps {
    api: Api;
    jobPost: M.CareerCenter.JobPost;
    onSuccess: (job: M.CareerCenter.JobPost) => void;
}

class ReviewJobForm extends ComponentWithValidatedForm<ReviewJobFormProps, {}> {

    private _isMounted = false;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.CareerCenter.ReviewJob();
        formModel.data.guid = this.props.jobPost.guid;
        this.state = {
            ...this.getInitialState(formModel),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        let memberRef = null;
        const job = this.props.jobPost;
        const editLink = <Link to={Paths.CareerCenter.Job.Post + "/" + job.guid}>Edit</Link>;

        if (job.refMember && (!job.postedBy || job.refMember.userId !== job.postedBy.id))
            memberRef = <div>
                <hr />
                <h2>ASRS Member Reference</h2>
                <Comp.Editors.FormField name="" label="Reference">
                    {M.Member.MemberHelper.fullName(job.refMember, true)}
                    <br />{job.refMember.username} &ndash; {editLink}
                </Comp.Editors.FormField>
            </div>;

        return <div className="job-post">
            <h2>Position Details</h2>

            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                schema={this.state.formModel.rules}
                onValidSubmit={(model) => this.onSubmit(model)}
            >
                <Comp.Editors.Hidden formComponent={this} name="guid" />

                <Comp.Editors.FormField name="" label="Job category">
                    {job.category || "Not available"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="" label="Position title">
                    {job.title || "Not available"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="" label="Company/Organization">
                    {job.organization || "Not available"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="" label="Location">
                    <div dangerouslySetInnerHTML={{ __html: M.Generic.AddressHelper.displayFormatted(job.location) }} />
                    {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="" label="Description">
                    <div dangerouslySetInnerHTML={{ __html: job.description || "Not available" }} />
                    {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="" label="How to apply">
                    <div dangerouslySetInnerHTML={{ __html: job.howToApply || "Not available"}} />
                    {editLink}
                </Comp.Editors.FormField>

                {memberRef}

                <hr />
                <h2>Candidate Alerts</h2>

                <Comp.Editors.FormField name="" label="Subscribe">
                    {job.subscribed ? "Yes" : "No"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="">
                    By pressing submit, you agree that this job listing will be posted on the {Settings.ClientNameAbbreviation} Career Center in accordance
                    with {Settings.ClientName} <Link to={Settings.AsrsLegacyUrl + Paths.Page.PrivacyPolicy}>Privacy Policy</Link> and
                    <Link to={Settings.AsrsLegacyUrl + Paths.Page.TermsOfUse}>Terms of Use</Link>.
                </Comp.Editors.FormField>

                <Comp.Editors.SubmitField
                    cancelLink={Paths.Home}
                    showProcessing={this.state.formProcessing}
                />
            </Comp.Validation.ValidatedForm>
        </div>;
    }

    private async onSubmit(model: Forms.CareerCenter.ReviewJobData) {
        this.toggleProcessing(true);

        const qp = { queryParams: { guid: model.guid }};
        const result = await this.props.api.post<M.CareerCenter.JobPost>(Api.endpoints.users.careerCenter.finalizeJob, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.props.onSuccess(result.data);
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }

        if (this._isMounted)
            this.toggleProcessing(false);
    }
}



// WEBPACK FOOTER //
// ./src/containers/careerCenter/reviewJob/ReviewJob.tsx